export const BRAND_FONTS = [
  { id: "outfit", label: "Outfit", stack: "var(--font-outfit), ui-sans-serif, system-ui, sans-serif" },
  { id: "dm-sans", label: "DM Sans", stack: "var(--font-dm-sans), ui-sans-serif, system-ui, sans-serif" },
  { id: "ibm-plex-sans", label: "IBM Plex Sans", stack: "var(--font-ibm-plex-sans), ui-sans-serif, system-ui, sans-serif" },
  { id: "libre-baskerville", label: "Libre Baskerville", stack: "var(--font-libre-baskerville), ui-serif, Georgia, serif" },
  { id: "source-serif-4", label: "Source Serif 4", stack: "var(--font-source-serif-4), ui-serif, Georgia, serif" },
] as const;

export type BrandFontId = (typeof BRAND_FONTS)[number]["id"];

export type BrandTheme = {
  pageBackgroundFrom: string;
  pageBackgroundTo: string;
  infoPanelBackground: string;
  rsvpPanelBackground: string;
  fontFamily: BrandFontId;
  buttonBackground: string;
  buttonForeground: string;
};

export const DEFAULT_BRAND_THEME: BrandTheme = {
  pageBackgroundFrom: "#f7f7f5",
  pageBackgroundTo: "#efefec",
  infoPanelBackground: "#ffffff",
  rsvpPanelBackground: "#ffffff",
  fontFamily: "outfit",
  buttonBackground: "#171717",
  buttonForeground: "#fafafa",
};

const FONT_IDS = new Set<string>(BRAND_FONTS.map((f) => f.id));

export function isHexColor(value: unknown): value is string {
  return typeof value === "string" && /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(value);
}

/** Native `<input type="color">` needs 6-digit hex. */
export function normalizeHexColor(value: string): string {
  const hex = value.toLowerCase();
  if (/^#[0-9a-f]{6}$/.test(hex)) return hex;
  if (/^#[0-9a-f]{3}$/.test(hex)) {
    const r = hex[1];
    const g = hex[2];
    const b = hex[3];
    return `#${r}${r}${g}${g}${b}${b}`;
  }
  return value;
}

export function parseBrandTheme(raw: string | null | undefined): BrandTheme {
  if (!raw || raw === "{}") return { ...DEFAULT_BRAND_THEME };
  try {
    const parsed = JSON.parse(raw) as Partial<BrandTheme>;
    return {
      pageBackgroundFrom: isHexColor(parsed.pageBackgroundFrom)
        ? parsed.pageBackgroundFrom
        : DEFAULT_BRAND_THEME.pageBackgroundFrom,
      pageBackgroundTo: isHexColor(parsed.pageBackgroundTo)
        ? parsed.pageBackgroundTo
        : DEFAULT_BRAND_THEME.pageBackgroundTo,
      infoPanelBackground: isHexColor(parsed.infoPanelBackground)
        ? parsed.infoPanelBackground
        : DEFAULT_BRAND_THEME.infoPanelBackground,
      rsvpPanelBackground: isHexColor(parsed.rsvpPanelBackground)
        ? parsed.rsvpPanelBackground
        : DEFAULT_BRAND_THEME.rsvpPanelBackground,
      fontFamily: FONT_IDS.has(parsed.fontFamily ?? "")
        ? (parsed.fontFamily as BrandFontId)
        : DEFAULT_BRAND_THEME.fontFamily,
      buttonBackground: isHexColor(parsed.buttonBackground)
        ? parsed.buttonBackground
        : DEFAULT_BRAND_THEME.buttonBackground,
      buttonForeground: isHexColor(parsed.buttonForeground)
        ? parsed.buttonForeground
        : DEFAULT_BRAND_THEME.buttonForeground,
    };
  } catch {
    return { ...DEFAULT_BRAND_THEME };
  }
}

export function serializeBrandTheme(theme: BrandTheme): string {
  return JSON.stringify(theme);
}

export function fontStack(fontFamily: BrandFontId): string {
  return (
    BRAND_FONTS.find((f) => f.id === fontFamily)?.stack ?? BRAND_FONTS[0].stack
  );
}

export function pageBackgroundCss(theme: BrandTheme): string {
  if (theme.pageBackgroundFrom === theme.pageBackgroundTo) {
    return theme.pageBackgroundFrom;
  }
  return `linear-gradient(180deg, ${theme.pageBackgroundFrom} 0%, ${theme.pageBackgroundTo} 100%)`;
}

/** Inline style bag for the public splash root. */
export function brandCssVars(theme: BrandTheme): Record<string, string> {
  return {
    "--brand-page-bg": pageBackgroundCss(theme),
    "--brand-info-panel": theme.infoPanelBackground,
    "--brand-rsvp-panel": theme.rsvpPanelBackground,
    "--brand-button-bg": theme.buttonBackground,
    "--brand-button-fg": theme.buttonForeground,
    "--brand-font": fontStack(theme.fontFamily),
    "--primary": theme.buttonBackground,
    "--primary-foreground": theme.buttonForeground,
    fontFamily: "var(--brand-font)",
    background: "var(--brand-page-bg)",
  };
}

export type SiteBranding = {
  logoUrl: string;
  theme: BrandTheme;
};

export const DEFAULT_SITE_BRANDING: SiteBranding = {
  logoUrl: "",
  theme: { ...DEFAULT_BRAND_THEME },
};
